import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  IconButton,
  Box,
  FormHelperText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  postEmployeeExperience,
  getEmployeeExperience,
  putEmployeeExperience,
  deleteEmployeeExperience,
} from "app/services/Services";
import { useParams } from "react-router-dom";
import {
  generateErrorMessage,
  generateSuccessMessage,
} from "app/utils/GenerateMessage";
import ConfirmModal from "./ConfirmModal";
import {
  T,
  sectionCardSx,
  muiFieldSx,
  SectionHeader,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  LoadingBlock,
  ErrorBlock,
} from "./shared/formKit";

const experienceSchema = Yup.object().shape({
  experience: Yup.array()
    .of(
      Yup.object().shape({
        companyName: Yup.string().required("Company name is required"),
        role: Yup.string().required("Role/Position is required"),
        startDate: Yup.date().required("Start date is required"),
        endDate: Yup.date()
          .nullable()
          .test("end-date-validation", "End date must be after start date", function (value) {
            const { startDate } = this.parent;
            if (!value) return true;
            return !startDate || new Date(value) > new Date(startDate);
          }),
      })
    )
    .min(1, "At least one experience entry is required"),
});

const ExperienceDetails = ({ onCompleted = null }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [existingExperience, setExistingExperience] = useState([]);
  const [employeeId] = useState(id);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isEdit = existingExperience.length > 0;

  useEffect(() => {
    loadExistingExperience();
  }, [id]);

  const loadExistingExperience = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await getEmployeeExperience(id);

      if (response.status === 200 && response.data?.status === "success") {
        setExistingExperience(response.data.data || []);
      } else {
        setExistingExperience([]);
      }
    } catch (err) {
      console.error("Error fetching experience:", err);
      setExistingExperience([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getInitialValues = () => {
    if (isEdit) {
      return {
        experience: existingExperience.map((e) => ({
          id: e.id,
          companyName: e.companyName || "",
          role: e.role || "",
          startDate: e.startDate ? e.startDate.split("T")[0] : "",
          endDate: e.endDate ? e.endDate.split("T")[0] : "",
        })),
      };
    }
    return { experience: [{ companyName: "", role: "", startDate: "", endDate: "" }] };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const updated = values.experience.filter((e) => e.id);
      const created = values.experience.filter((e) => !e.id);

      if (updated.length) {
        await putEmployeeExperience({
          experience: updated.map((e) => ({
            id: e.id,
            companyName: e.companyName,
            role: e.role,
            startDate: e.startDate,
            endDate: e.endDate || null,
          })),
        });
      }

      if (created.length) {
        await postEmployeeExperience(employeeId, {
          employeeId,
          experience: created.map((e) => ({
            companyName: e.companyName,
            role: e.role,
            startDate: e.startDate,
            endDate: e.endDate || null,
          })),
        });
      }

      generateSuccessMessage("Experience records saved successfully");
      await loadExistingExperience();
      if (onCompleted) onCompleted(values.experience);
    } catch (err) {
      console.error(err);
      generateErrorMessage(err.response?.data?.message || "Failed to save experience");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (experienceId, removeFromForm) => {
    if (!experienceId) {
      removeFromForm();
      return;
    }
    setDeleteTarget({ experienceId, removeFromForm });
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { experienceId } = deleteTarget;

    try {
      setLoading(true);
      await deleteEmployeeExperience([experienceId]);
      generateSuccessMessage("Record deleted successfully");
      await loadExistingExperience();
      setConfirmModalOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      generateErrorMessage(err.response?.data?.message || "Failed to delete record");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={sectionCardSx}>
        <LoadingBlock label="Loading experience data…" />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && <ErrorBlock message="Failed to load experience data." onRetry={loadExistingExperience} />}

      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={experienceSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <FieldArray name="experience">
              {({ push, remove }) => (
                <Box sx={sectionCardSx}>
                  <SectionHeader
                    title="Work experience"
                    action={
                      <SecondaryButton
                        type="button"
                        onClick={() => push({ companyName: "", role: "", startDate: "", endDate: "" })}
                        disabled={isSubmitting}
                      >
                        <AddIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: "6px" }} />
                        Add experience
                      </SecondaryButton>
                    }
                  />

                  {values.experience.map((exp, index) => (
                    <Box
                      key={`${exp.id || "new"}-${index}`}
                      sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: T.inputR, padding: "16px 18px", mb: "16px" }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Box sx={{ fontFamily: T.body_f, fontSize: "13.5px", fontWeight: 600, color: T.ink }}>
                            Experience {index + 1}
                          </Box>
                          <StatusChip label={exp.id ? "Existing" : "New"} tone={exp.id ? "neutral" : "warning"} />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(exp.id, () => remove(index))}
                          disabled={isSubmitting || loading}
                          sx={{ color: T.danger }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            name={`experience.${index}.companyName`} label="Company name"
                            value={exp.companyName} onChange={handleChange} onBlur={handleBlur}
                            error={touched.experience?.[index]?.companyName && Boolean(errors.experience?.[index]?.companyName)}
                            helperText={touched.experience?.[index]?.companyName && errors.experience?.[index]?.companyName}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            name={`experience.${index}.role`} label="Role/Position"
                            value={exp.role} onChange={handleChange} onBlur={handleBlur}
                            error={touched.experience?.[index]?.role && Boolean(errors.experience?.[index]?.role)}
                            helperText={touched.experience?.[index]?.role && errors.experience?.[index]?.role}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            type="date" name={`experience.${index}.startDate`} label="Start date"
                            InputLabelProps={{ shrink: true }}
                            value={exp.startDate} onChange={handleChange} onBlur={handleBlur}
                            error={touched.experience?.[index]?.startDate && Boolean(errors.experience?.[index]?.startDate)}
                            helperText={touched.experience?.[index]?.startDate && errors.experience?.[index]?.startDate}
                            inputProps={{ max: new Date().toISOString().split("T")[0] }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            type="date" name={`experience.${index}.endDate`} label="End date (leave empty if current job)"
                            InputLabelProps={{ shrink: true }}
                            value={exp.endDate} onChange={handleChange} onBlur={handleBlur}
                            error={touched.experience?.[index]?.endDate && Boolean(errors.experience?.[index]?.endDate)}
                            helperText={touched.experience?.[index]?.endDate && errors.experience?.[index]?.endDate}
                            inputProps={{ max: new Date().toISOString().split("T")[0] }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {typeof errors.experience === "string" && (
                    <FormHelperText error>{errors.experience}</FormHelperText>
                  )}

                  <Box mt={2} display="flex" gap={2}>
                    <PrimaryButton disabled={isSubmitting || !values.experience.length}>
                      {isSubmitting ? "Saving…" : "Save / update"}
                    </PrimaryButton>
                    <SecondaryButton type="button" onClick={loadExistingExperience} disabled={isSubmitting}>
                      Refresh
                    </SecondaryButton>
                  </Box>
                </Box>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleConfirmDelete}
        title="Delete Experience Record?"
        description="Are you sure you want to delete this record? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />
    </Box>
  );
};

export default ExperienceDetails;
